import classNames from 'classnames/bind';
import { ChangeEvent, useEffect, useRef, useState } from 'react';

import styles from './Geocode.module.scss';
import TextInput from 'components/helpers/TextInput/TextInput';

import { dataFetcherGet } from 'lib/data-fetcher';
import { GeocodeResponse } from './Types/Geocode.Type';

type Suggestion = {
  lat: number;
  lng: number;
  address: string;
};
export type AutoCompleteProps = {
  headerTitle: string;
};

const AutoComplete: React.FC<AutoCompleteProps> = (props) => {
  const cx = classNames.bind(styles);

  const [userAddress, setUserAddress] = useState('');
  const [error, setError] = useState('');
  const [coordinates, setCoordinates] = useState<Suggestion[]>([]);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const skipSuggestionAfterSelect = useRef(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (skipSuggestionAfterSelect.current) {
      skipSuggestionAfterSelect.current = false;
      return;
    }

    if (!userAddress || userAddress.length < 3) {
      setSuggestions([]);
      return;
    }

    let isActive = true;

    const debounceTimer = setTimeout(async () => {
      const results = await fetchAddressSuggestions(userAddress);
      if (isActive) setSuggestions(results);
    }, 300);

    return () => {
      isActive = false;
      clearTimeout(debounceTimer);
    };
  }, [userAddress]);

  const fetchAddressSuggestions = async (query: string): Promise<Suggestion[]> => {
    try {
      const autoCompleteApiUrl = `/api/maps/autoCompleteByAzureMapService?query=${encodeURIComponent(query)}`;
      const response = await dataFetcherGet<GeocodeResponse>(autoCompleteApiUrl);

      const results = response?.data?.geocodeData?.results || [];

      return results.map((result) => {
        const [lng, lat] = result.geometry?.coordinates || [];

        return {
          lat,
          lng,
          address: result.properties?.address?.formattedAddress || 'Unknown address',
        };
      });
    } catch (err) {
      console.error(err);
      setError('Something went wrong. Please try again.');
      return [];
    }
  };

  const handleSelectSuggestion = async (s: Suggestion) => {
    skipSuggestionAfterSelect.current = true;

    setUserAddress(s.address);
    setSuggestions([]);
    setError('');

    try {
      setLoading(true);
      const geocodeApiUrl = `/api/maps/geocodeByAzureMapService?query=${encodeURIComponent(s.address.trim())}`;
      const response = await dataFetcherGet<GeocodeResponse>(geocodeApiUrl);

      const results = response?.data?.geocodeData?.results;
      if (results?.length > 0) {
        const mappedResults = results.map((result) => {
          const [lng, lat] = result.geometry?.coordinates || [];
          return {
            lat,
            lng,
            address: result.properties?.address?.formattedAddress || 'Unknown address',
          };
        });
        setCoordinates(mappedResults);
      } else {
        setCoordinates([]);
        setError('No results found for the provided address.');
      }
    } catch (error) {
      console.error('Error fetching location:', error);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  function handleUserAddressChange(e: ChangeEvent<HTMLInputElement>): void {
    const enteredValue = e.target.value;
    setUserAddress(enteredValue);

    if (!enteredValue.trim()) {
      setCoordinates([]);
      setSuggestions([]);
      setError('');
    }
  }

  return (
    <div className={cx('address-search')}>
      <h2 className={cx('header')}>{props.headerTitle}</h2>
      <label htmlFor="userAddress" className={cx('address-label')}>
        Enter Address
      </label>
      <div className={cx('input-wrapper')}>
        <TextInput
          value={userAddress}
          type="text"
          id="userAddress"
          autoComplete="off"
          onChange={(e) => handleUserAddressChange(e)}
          placeholder="Zip or City/State or full address"
          className={cx('inputBox')}
        />

        {loading && <span> Searching...</span>}

        {suggestions.length > 0 && (
          <ul className={cx('suggestions-dropdown')}>
            {suggestions.map((s, id) => (
              <li key={id} className={cx('suggestion-item')} onClick={() => handleSelectSuggestion(s)}>
                {s.address}
              </li>
            ))}
          </ul>
        )}
      </div>
      {error && <p className={cx('error-msg')}>{error}</p>}

      {coordinates.map((item, index) => (
        <div className={cx('coordinates')} key={index}>
          <p>
            <span className={cx('coordinate-label')}>Latitude:</span> {item.lat}
          </p>
          <p>
            <span className={cx('coordinate-label')}>Longitude:</span> {item.lng}
          </p>
          <p>
            <span className={cx('coordinate-label')}>Address:</span> {item.address}
          </p>
        </div>
      ))}
    </div>
  );
};

export default AutoComplete;
